/**
 * DishEditor Drawer (Uber Eats Style)
 * 
 * Stabile Version: Fixes crash beim Anlegen durch sauberen Payload-Sync.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Plus,
  Save,
  Trash2,
  RefreshCw,
  Info,
  ChevronRight,
} from "lucide-react";
import type {
  Allergens,
  Dish,
  DishCategory,
  DishOption,
  DishOptionItem,
  DishType,
} from "@tasty/api";
import {
  DishClient,
  DishOptionClient,
  removeTypename,
} from "../../api-client";
import uploadImage from "../../utils/uploadImage";
import S3Image from "../../S3Image";
import { TERMS } from "@tasty/shared";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { cn, formatPrice } from "@/lib/utils";

const COMMON_ALLERGENS = [
  "Gluten", "Milch/Laktose", "Eier", "Nüsse", "Erdnüsse", "Soja", "Fisch",
  "Krebstiere", "Sellerie", "Senf", "Sesam", "Sulfite", "Lupinen", "Weichtiere"
];

interface DishEditorDrawerProps {
  dishId: string | null;
  restaurantId: string;
  categories: DishCategory[];
  onClose: () => void;
  onSaved: (dish: Dish) => void;
  onDeleted: (dishId: string) => void;
}

export function DishEditorDrawer({
  dishId,
  restaurantId,
  categories,
  onClose,
  onSaved,
  onDeleted,
}: DishEditorDrawerProps) {
  const isEditMode = Boolean(dishId);
  const [dish, setDish] = useState<Dish | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    dishType: "FOOD" as DishType,
    soldOut: false,
    categoryId: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [allergens, setAllergens] = useState<Array<Omit<Allergens, "__typename">>>([]);
  const [newAllergen, setNewAllergen] = useState("");

  const [options, setOptions] = useState<DishOption[]>([]);

  const [imageKey, setImageKey] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const parsePrice = (raw: string) => {
    const p = parseFloat(String(raw).replace(",", ".").trim());
    return isNaN(p) ? 0 : p;
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = "Name erforderlich";
    if (!form.description.trim()) next.description = "Beschreibung erforderlich";
    const p = parsePrice(form.price);
    if (p < 0) next.price = "Ungültiger Preis";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const loadData = useCallback(async () => {
    if (!dishId) {
      setDish(null);
      setForm({
        name: "",
        description: "",
        price: "",
        dishType: "FOOD" as DishType,
        soldOut: false,
        categoryId: "",
      });
      setAllergens([]);
      setImageKey(null);
      setImageFile(null);
      setImagePreview(null);
      return;
    }
    setLoading(true);
    try {
      const d = await DishClient.queryItem(dishId);
      if (d) {
        setDish(d);
        setForm({
          name: d.name || "",
          description: d.description || "",
          price: String(d.price || ""),
          dishType: (d.dishType || "FOOD") as DishType,
          soldOut: Boolean(d.soldOut),
          categoryId: d.dishCategoryId || "",
        });
        setAllergens(removeTypename(d.allergens || []).filter(Boolean) as any);
        setImageKey(d.image || null);
        setImageFile(null);
        setImagePreview(null);

        const opts = await DishOptionClient.queryList({ filter: { restaurantID: { eq: restaurantId } } });
        setOptions(opts || []);
      }
    } finally {
      setLoading(false);
    }
  }, [dishId, restaurantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const assignedOptions = useMemo(() => {
    if (!dish?.id) return [];
    return options.filter(o => o.dishIDs?.includes(dish.id));
  }, [dish?.id, options]);

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Bitte prüfe deine Eingaben");
      return;
    }
    setSaving(true);
    try {
      const priceNum = parsePrice(form.price);

      // Bild hochladen falls neu gewaehlt, sonst bestehenden Key behalten
      let imageKeyToSave = imageKey;
      if (imageFile) {
        const filename = `${restaurantId}/dishes/${uuidv4()}`;
        const uploaded = await uploadImage(filename, imageFile, imageKey, 1000);
        if (uploaded) imageKeyToSave = uploaded;
      }

      // Senior-Safe: allergens direkt im Create/Update mitsenden (verhindert Race-Conditions)
      const input: any = {
        name: form.name.trim(),
        description: form.description.trim(),
        price: priceNum,
        dishType: form.dishType,
        soldOut: form.soldOut,
        dishCategoryId: form.categoryId || null, // null instead of undefined for clean payload
        restaurantID: restaurantId,
      };

      if (imageKeyToSave) {
        input.image = imageKeyToSave;
      }

      if (allergens.length > 0) {
        input.allergens = removeTypename(allergens);
      }

      const saved = isEditMode && dish
        ? await DishClient.updateItem({ id: dish.id, ...input })
        : await DishClient.createItem(input);

      if (saved) {
        toast.success(isEditMode ? `${TERMS.product} aktualisiert` : `${TERMS.product} erstellt`);
        onSaved(saved);
        onClose();
      } else {
        throw new Error("API returned null");
      }
    } catch (e) {
      console.error("Save error:", e);
      toast.error("Speichern fehlgeschlagen (Backend-Fehler)");
    } finally {
      setSaving(false);
    }
  };

  const addAllergen = (name: string) => {
    const n = name.trim();
    if (!n || allergens.some(a => a.name?.toLowerCase() === n.toLowerCase())) return;
    setAllergens([...allergens, { id: uuidv4(), name: n }]);
    setNewAllergen("");
  };

  if (loading && isEditMode) {
    return (
      <SheetContent className="flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </SheetContent>
    );
  }

  return (
    <SheetContent className="flex flex-col h-full sm:max-w-xl p-0 overflow-hidden border-l shadow-2xl">
      <SheetHeader className="p-6 border-b shrink-0 bg-muted/5">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <SheetTitle className="truncate text-xl font-bold tracking-tight">
              {isEditMode ? form.name || "Bearbeiten" : `Neues ${TERMS.product}`}
            </SheetTitle>
            <SheetDescription className="text-xs font-medium uppercase tracking-widest text-muted-foreground/60 mt-1">
              {isEditMode ? "Artikel-Details anpassen" : `${TERMS.catalog} erweitern`}
            </SheetDescription>
          </div>
          <Button onClick={handleSave} disabled={saving} size="sm" className="font-bold px-6 shadow-sm">
            {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-muted space-y-10">
        <div className="space-y-8 pb-12">
          {/* Photo Section */}
          <section className="space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Produktfoto</h3>
            <label className="aspect-video rounded-2xl border-2 border-dashed flex flex-col items-center justify-center bg-muted/10 hover:bg-muted/20 transition-all cursor-pointer group border-muted-foreground/20 overflow-hidden relative">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Vorschau"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : imageKey ? (
                <div className="absolute inset-0 [&_.ant-image]:!w-full [&_.ant-image]:!h-full [&_img]:!w-full [&_img]:!h-full [&_img]:!object-cover">
                  <S3Image imgKey={imageKey} preview={false} />
                </div>
              ) : (
                <>
                  <Plus className="h-8 w-8 text-muted-foreground/30 group-hover:scale-110 transition-transform mb-2" />
                  <p className="text-xs text-muted-foreground/60 font-semibold uppercase tracking-wide">
                    Foto hinzufügen
                  </p>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setImageFile(f);
                    setImagePreview(URL.createObjectURL(f));
                  }
                }}
              />
            </label>
          </section>

          {/* Basisdaten */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Stammdaten</h3>
            <div className="grid gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground/70 ml-1">Bezeichnung *</label>
                <Input 
                  value={form.name} 
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="z.B. Avocado Burger"
                  className={cn("h-11 rounded-xl bg-muted/5 border-muted-foreground/10 focus:bg-background transition-all font-medium", errors.name && "border-destructive ring-destructive/20")}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground/70 ml-1">Verkaufspreis (€) *</label>
                <div className="relative">
                  <Input 
                    value={form.price} 
                    onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                    placeholder="0.00"
                    className={cn("h-11 pr-10 rounded-xl bg-muted/5 border-muted-foreground/10 focus:bg-background transition-all font-bold tabular-nums", errors.price && "border-destructive ring-destructive/20")}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/60 font-bold">€</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground/70 ml-1">Beschreibung *</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  className={cn(
                    "w-full min-h-[100px] rounded-xl border border-muted-foreground/10 bg-muted/5 focus:bg-background px-4 py-3 text-sm focus:ring-2 focus:ring-ring transition-all outline-none font-medium",
                    errors.description && "border-destructive ring-destructive/20"
                  )}
                  placeholder="Zutaten, Besonderheiten, Zubereitung..."
                />
              </div>
            </div>
          </section>

          {/* Category */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Kategorie</h3>
            <select
              className="w-full h-11 rounded-xl border border-muted-foreground/10 bg-muted/5 px-4 text-sm focus:ring-2 focus:ring-ring transition-all outline-none font-semibold cursor-pointer"
              value={form.categoryId}
              onChange={e => setForm(p => ({ ...p, categoryId: e.target.value }))}
            >
              <option value="">Ohne Zuordnung</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </section>

          {/* Availability */}
          <section className="space-y-4">
            <div className="flex items-center justify-between p-5 rounded-2xl border border-muted-foreground/10 bg-muted/5 shadow-sm">
              <div className="space-y-0.5">
                <p className="text-sm font-bold">Verfügbarkeit</p>
                <p className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wide">Aktuell bestellbar</p>
              </div>
              <Switch checked={!form.soldOut} onCheckedChange={v => setForm(p => ({ ...p, soldOut: !v }))} />
            </div>
          </section>

          {/* Allergens */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Allergene & Kennzeichnung</h3>
            <div className="flex flex-wrap gap-1.5">
              {COMMON_ALLERGENS.slice(0, 8).map(a => (
                <Badge 
                  key={a} 
                  variant="outline" 
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-all px-3 py-1 rounded-full text-[10px] font-bold border-muted-foreground/20"
                  onClick={() => addAllergen(a)}
                >
                  {a}
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input 
                value={newAllergen} 
                onChange={e => setNewAllergen(e.target.value)} 
                placeholder="Eigene Angabe..."
                className="h-10 rounded-xl bg-muted/5 border-muted-foreground/10 font-medium"
                onKeyDown={e => e.key === 'Enter' && addAllergen(newAllergen)}
              />
              <Button size="sm" variant="outline" onClick={() => addAllergen(newAllergen)} className="rounded-xl h-10 px-4 font-bold">
                Hinzufügen
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {allergens.length > 0 && allergens.map(a => (
                <Badge key={a.id} className="bg-primary/10 text-primary border-primary/20 px-3 py-1 rounded-full text-xs font-bold animate-in zoom-in-95 group">
                  {a.name}
                  <button onClick={() => setAllergens(prev => prev.filter(x => x.id !== a.id))} className="ml-2 hover:scale-125 transition-transform font-bold">×</button>
                </Badge>
              ))}
            </div>
          </section>

          {/* Options (Customizations) */}
          <section className="space-y-4 pt-6 border-t border-dashed border-muted-foreground/20">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Customisations (Optionen)</h3>
            {!isEditMode ? (
              <div className="flex items-start gap-3 p-5 border border-dashed rounded-2xl bg-muted/5 border-muted-foreground/20 text-muted-foreground">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p className="text-[11px] leading-relaxed font-medium uppercase tracking-wide">
                  Speichere das {TERMS.product} zuerst, um Modifier-Groups zuzuweisen.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {assignedOptions.length === 0 ? (
                  <div className="text-center py-8 border rounded-2xl border-dashed bg-muted/5 border-muted-foreground/10">
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-widest">Keine Modifier Groups</p>
                  </div>
                ) : (
                  assignedOptions.map(opt => (
                    <div key={opt.id} className="flex items-center justify-between p-4 border rounded-xl bg-card hover:border-primary/30 transition-all group cursor-pointer border-muted-foreground/10 shadow-sm">
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-bold truncate">{opt.title}</span>
                        <span className="text-[10px] text-muted-foreground font-semibold uppercase">Modifier Group</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </div>
                  ))
                )}
                <Button variant="outline" className="w-full text-[10px] h-10 border-dashed rounded-xl mt-3 font-bold uppercase tracking-widest bg-muted/5 hover:bg-muted/10 border-muted-foreground/20" onClick={() => toast.info("Nutze den Tab 'Optionen' für die Verwaltung")}>
                  Modifier Groups verwalten
                </Button>
              </div>
            )}
          </section>

          {/* Delete Action */}
          {isEditMode && (
            <section className="pt-12">
              <Button 
                variant="ghost" 
                className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive h-12 rounded-xl font-bold uppercase tracking-widest text-xs"
                onClick={() => {
                  if (confirm(`${TERMS.product} wirklich unwiderruflich löschen?`)) {
                    DishClient.deleteItem({ id: dishId!, _version: dish?._version }).then(() => onDeleted(dishId!));
                  }
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {TERMS.product} löschen
              </Button>
            </section>
          )}
        </div>
      </div>
    </SheetContent>
  );
}
